/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { apiClient } from './client.js';

export type SessionRequestStatus = 'pending' | 'approved' | 'declined';

export interface SessionRequest {
  readonly id: string;
  readonly clientId: string;
  readonly clientName: string | null;
  readonly requestedByUserId: string | null;
  readonly requestedByName: string | null;
  readonly preferredDate: string | null;
  readonly preferredTime: string | null;
  readonly therapyType: string | null;
  readonly notes: string | null;
  readonly status: SessionRequestStatus;
  readonly adminNote: string | null;
  readonly sessionId: string | null;
  readonly respondedAt: string | null;
  readonly createdAt: string;
}

export interface CreateSessionRequestPayload {
  readonly clientId: string;
  readonly preferredDate?: string;
  readonly preferredTime?: string;
  readonly therapyType?: string;
  readonly notes?: string;
}

export interface ApproveSessionRequestPayload {
  readonly scheduledStart: string;
  readonly therapistId?: string;
  readonly roomId?: string;
  readonly sessionType?: string;
  readonly adminNote?: string;
}

const BASE = '/session-requests';

export const sessionRequestsApi = {
  async listAll(status?: SessionRequestStatus): Promise<readonly SessionRequest[]> {
    const { data } = await apiClient.get<readonly SessionRequest[]>(BASE, {
      params: status !== undefined ? { status } : {},
    });
    return data;
  },
  async listMine(): Promise<readonly SessionRequest[]> {
    const { data } = await apiClient.get<readonly SessionRequest[]>(`${BASE}/mine`);
    return data;
  },
  async create(payload: CreateSessionRequestPayload): Promise<SessionRequest> {
    const { data } = await apiClient.post<SessionRequest>(BASE, payload);
    return data;
  },
  async approve(id: string, payload: ApproveSessionRequestPayload): Promise<SessionRequest> {
    const { data } = await apiClient.patch<SessionRequest>(`${BASE}/${id}/approve`, payload);
    return data;
  },
  async decline(id: string, adminNote?: string): Promise<SessionRequest> {
    const { data } = await apiClient.patch<SessionRequest>(`${BASE}/${id}/decline`, { adminNote });
    return data;
  },
};
